import { createContext, useContext, useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useAuthContext } from './AuthContext'
import { useFilesContext } from './FilesContext'

// Create the HistoryContext to hold history-related state and functions
const HistoryContext = createContext()

// Custom hook to use the HistoryContext
export const useHistoryContext = () => useContext(HistoryContext)

// HistoryProvider component to provide the HistoryContext to its children
export const HistoryProvider = ({ children }) => {
  // State to hold the history of requests and responses
  const [history, setHistory] = useState([])
  // State to hold the current session ID
  const [sessionId, setSessionId] = useState(() => uuidv4())
  // Get authentication status and cookie getter from AuthContext
  const { isAuthenticated } = useAuthContext()

  const { files } = useFilesContext()


  // URL for the API endpoint, defaulting to an empty string if not defined
  const url = process.env.REACT_APP_API_URL || ''

  // Function to add a request to the history
  const addRequest = (message, item, { persist = true } = {}) => {
    const newEntry = {
      type: 'request',
      content: message,
      sessionId,
      feed_metadata: files
        ? files.map((file) => ({ url: file.url, category: file.category }))
        : null,
    }
    const oldEntry = {
      type: 'request',
      content: message,
      sessionId,
      feed_metadata: files
        ? files.map((file) => ({ url: file.url, category: file.category }))
        : null,
    }

    // If there's no existing item, add the new entry to the history
    if (!item) {
      setHistory((prevHistory) => [...prevHistory, newEntry])
    } else {
      // If there is an existing item, add the old entry and update session ID
      setHistory((prevHistory) => [...prevHistory, oldEntry])
      setSessionId(item.session_id)
    }

    // If the user is authenticated and there's no existing item, save to database
    if (isAuthenticated && !item && persist) {
      saveHistoryToDatabase(newEntry)
    }
  }

  // Function to add a response to the history
  const addResponse = (message, item, { persist = true } = {}) => {
    const newEntry = { type: 'response', content: message, sessionId }
    const oldEntry = { type: 'response', content: message, sessionId }

    // If there's no existing item, add the new entry to the history
    if (!item) {
      setHistory((prevHistory) => [...prevHistory, newEntry])
    } else {
      // If there is an existing item, add the old entry and update session ID
      setHistory((prevHistory) => [...prevHistory, oldEntry])
      setSessionId(item.session_id)
    }

    // If the user is authenticated and there's no existing item, save to database
    if (isAuthenticated && !item && persist) {
      saveHistoryToDatabase(newEntry)
    }
  }

  // Function to save history entry to the database
  const saveHistoryToDatabase = async (entry) => {
    const formData = {
      sessionId: entry.sessionId,
      type: entry.type,
      content: entry.content,
      feed_metadata: entry.feed_metadata ? JSON.stringify(entry.feed_metadata) : null,
    }

    try {
      const response = await fetch(`${url}/users/history`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        throw new Error('Failed to save history to database')
      }
    } catch (error) {
      console.error('Error saving history to database:', error)
    }
  }

  const updateLastResponse = (text) => {
    setHistory((prev) => {
      const updated = [...prev]
      const lastIndex = updated.map((item) => item.type).lastIndexOf('response')
      if (lastIndex !== -1) {
        updated[lastIndex] = { ...updated[lastIndex], content: text }
      }
      return updated
    })
  }
 // Call this after streaming finishes to persist the final response
 const persistLastResponse = () => {
   setHistory((prev) => {
     const lastIndex = prev.map((i) => i.type).lastIndexOf('response')
     if (lastIndex !== -1 && isAuthenticated) {
       const entry = prev[lastIndex]
       // Persist the final form
       saveHistoryToDatabase(entry)
     }
     return prev
   })
 }

  // Provide the history-related state and functions to children components
  return (
    <HistoryContext.Provider
      value={{
        history,
        sessionId,
        addRequest,
        addResponse,
        setHistory,
        updateLastResponse,
        persistLastResponse,
      }}
    >
      {children}
    </HistoryContext.Provider>
  )
}
